import {
  type ChangeListener,
  SCOPE_PRIORITY,
  type ShortcutChangeEvent,
  type ShortcutDefinition,
  type ShortcutHelpGroup,
  type ShortcutInput,
  type ShortcutScope,
} from './types';

// ─── ID Generation ────────────────────────────────────────────────────────────

let _globalSeq = 0;
const nextSeq = () => ++_globalSeq;
const genId = () => `sc-${nextSeq()}`;

// ─── Combo Matching ───────────────────────────────────────────────────────────

function matchesCombo(event: KeyboardEvent, def: ShortcutDefinition): boolean {
  const { key, modifiers = [] } = def.combo;

  // Key check — case-insensitive. Support 'Escape'/'escape', 'Enter'/'enter', etc.
  if (event.key.toLowerCase() !== key.toLowerCase()) return false;

  // Modifier checks
  for (const mod of modifiers) {
    switch (mod) {
      case 'ctrl':
        // On macOS Cmd (meta) is the primary modifier — treat as equivalent to Ctrl.
        if (!event.ctrlKey && !event.metaKey) return false;
        break;
      case 'meta':
        if (!event.metaKey) return false;
        break;
      case 'alt':
        if (!event.altKey) return false;
        break;
      case 'shift':
        if (!event.shiftKey) return false;
        break;
    }
  }

  // Reject if unexpected modifiers are held by the user
  const wantsCtrl = modifiers.includes('ctrl') || modifiers.includes('meta');
  const wantsAlt = modifiers.includes('alt');
  const wantsShift = modifiers.includes('shift');

  if ((event.ctrlKey || event.metaKey) && !wantsCtrl) return false;
  if (event.altKey && !wantsAlt) return false;
  if (event.shiftKey && !wantsShift) return false;

  return true;
}

// ─── Target Helpers ───────────────────────────────────────────────────────────

/**
 * Returns true when the keyboard event originates inside an editable element
 * (input, textarea, select, or contentEditable).
 */
function isTextTarget(event: KeyboardEvent): boolean {
  const el = event.target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

/**
 * Returns true if the shortcut requires at least one primary modifier
 * (Ctrl/Cmd or Alt), which makes it safe to fire inside text inputs.
 */
function hasPrimaryModifier(def: ShortcutDefinition): boolean {
  const mods = def.combo.modifiers ?? [];
  return mods.includes('ctrl') || mods.includes('meta') || mods.includes('alt');
}

// ─── ShortcutRegistry ─────────────────────────────────────────────────────────

/**
 * Framework-agnostic registry for keyboard shortcut management.
 *
 * Responsibilities:
 *  - Store and manage shortcut definitions.
 *  - Track which scope IDs are currently active.
 *  - Dispatch keyboard events to the correct handler based on priority + LIFO.
 *  - Notify subscribers on any state change (for React useSyncExternalStore).
 *
 * React integration is in ShortcutManager.tsx and use-shortcuts.ts.
 * This class has zero React imports — it can be tested in isolation.
 */
export class ShortcutRegistry {
  private readonly _shortcuts = new Map<string, ShortcutDefinition>();
  private readonly _activeScopes = new Set<string>();
  private readonly _listeners = new Set<ChangeListener>();

  // Memoized snapshot for useSyncExternalStore stability
  private _visibleCache: readonly ShortcutDefinition[] | null = null;

  // ─── Registration ──────────────────────────────────────────────────────────

  /**
   * Register a shortcut and return its stable ID.
   * If `input.id` is supplied and already exists, the existing definition
   * is replaced (idempotent overwrite — useful for re-registration on dep change).
   */
  register(input: ShortcutInput): string {
    const id = input.id ?? genId();
    const seq = nextSeq();

    const def: ShortcutDefinition = {
      id,
      combo: { key: input.combo.key, modifiers: input.combo.modifiers },
      scope: input.scope,
      scopeId: input.scopeId,
      priority: input.priority ?? SCOPE_PRIORITY[input.scope],
      handler: input.handler,
      preventDefault: input.preventDefault ?? true,
      stopPropagation: input.stopPropagation ?? false,
      enabled: input.enabled ?? true,
      meta: input.meta,
      seq,
    };

    this._shortcuts.set(id, def);
    this._invalidateCache();
    this._emit({ type: 'registered', id });
    return id;
  }

  /** Remove a shortcut. No-op if ID does not exist. */
  unregister(id: string): void {
    if (!this._shortcuts.has(id)) return;
    this._shortcuts.delete(id);
    this._invalidateCache();
    this._emit({ type: 'unregistered', id });
  }

  /** Unregister multiple shortcuts at once. */
  unregisterAll(ids: string[]): void {
    let changed = false;
    for (const id of ids) {
      if (this._shortcuts.has(id)) {
        this._shortcuts.delete(id);
        changed = true;
      }
    }
    if (changed) {
      this._invalidateCache();
      this._emit({ type: 'unregistered' });
    }
  }

  // ─── Enable / Disable ──────────────────────────────────────────────────────

  /**
   * Enable a registered shortcut.
   * Mutates the definition in-place — no re-registration needed.
   */
  enable(id: string): void {
    const def = this._shortcuts.get(id);
    if (!def || def.enabled) return;
    def.enabled = true;
    this._emit({ type: 'enabled', id });
  }

  /** Disable a registered shortcut. */
  disable(id: string): void {
    const def = this._shortcuts.get(id);
    if (!def?.enabled) return;
    def.enabled = false;
    this._emit({ type: 'disabled', id });
  }

  toggle(id: string, enabled: boolean): void {
    if (enabled) {
      this.enable(id);
    } else {
      this.disable(id);
    }
  }

  // ─── Scope Management ──────────────────────────────────────────────────────

  /**
   * Activate a scope instance.
   * Only shortcuts with `scopeId` equal to this ID will become eligible.
   * Typically called on dialog/form/table mount.
   */
  activateScope(scopeId: string): void {
    if (this._activeScopes.has(scopeId)) return;
    this._activeScopes.add(scopeId);
    this._emit({ type: 'scope:activated', scopeId });
  }

  /** Deactivate a scope instance. Typically called on dialog/form/table unmount. */
  deactivateScope(scopeId: string): void {
    if (!this._activeScopes.has(scopeId)) return;
    this._activeScopes.delete(scopeId);
    this._emit({ type: 'scope:deactivated', scopeId });
  }

  isScopeActive(scopeId: string): boolean {
    return this._activeScopes.has(scopeId);
  }

  getActiveScopes(): ReadonlySet<string> {
    return this._activeScopes;
  }

  // ─── Dispatch ──────────────────────────────────────────────────────────────

  /**
   * Main dispatch entry point. Called by the keydown listener in
   * ShortcutManagerProvider. Returns true if at least one handler fired.
   *
   * Resolution rules:
   *
   * 1. Only enabled shortcuts whose combo matches the event are considered.
   * 2. If a shortcut has `scopeId`, that scope must be active. Shortcuts without
   *    `scopeId` are eligible as long as they are registered (React lifecycle
   *    ensures they're only registered when the component is mounted).
   * 3. Shortcuts with `scope === 'field'` only fire inside text input targets.
   * 4. Shortcuts without a primary modifier (Ctrl/Alt) never fire inside a text
   *    input target unless they are `scope === 'field'` (prevents hijacking typing).
   * 5. Among eligible scoped (non-global) handlers: the SINGLE highest-priority
   *    one fires (priority DESC, then seq DESC for LIFO tiebreaking).
   * 6. ALL global handlers fire, sorted by priority DESC then seq DESC.
   *    Global handlers are NEVER blocked by scoped handlers.
   */
  dispatch(event: KeyboardEvent): boolean {
    // Only handle trusted events (not programmatically dispatched)
    if (!event.isTrusted) return false;

    const inText = isTextTarget(event);

    // ── Collect candidates ────────────────────────────────────────────────────

    const scoped: ShortcutDefinition[] = [];
    const globals: ShortcutDefinition[] = [];

    for (const def of this._shortcuts.values()) {
      if (!def.enabled) continue;
      if (!matchesCombo(event, def)) continue;

      // 'field' scope: ONLY fire inside text targets
      if (def.scope === 'field' && !inText) continue;

      // Non-'field' scopes: DO NOT fire plain keys (no primary modifier) inside text targets
      // This prevents Enter/ArrowDown from interfering with normal typing
      if (inText && def.scope !== 'field' && !hasPrimaryModifier(def)) continue;

      // scopeId check: if set, scope instance must be active
      if (def.scopeId !== undefined && !this._activeScopes.has(def.scopeId)) continue;

      if (def.scope === 'global') {
        globals.push(def);
      } else {
        scoped.push(def);
      }
    }

    if (scoped.length === 0 && globals.length === 0) return false;

    // ── Sort: priority DESC, then seq DESC (LIFO tiebreaker) ─────────────────

    const compareFn = (a: ShortcutDefinition, b: ShortcutDefinition): number =>
      b.priority !== a.priority ? b.priority - a.priority : b.seq - a.seq;

    scoped.sort(compareFn);
    globals.sort(compareFn);

    let handled = false;

    // ── Fire the single highest-priority scoped handler ────────────────────────

    if (scoped.length > 0) {
      const winner = scoped[0];
      if (winner.preventDefault) event.preventDefault();
      if (winner.stopPropagation) event.stopPropagation();
      winner.handler(event);
      handled = true;
    }

    // ── Fire ALL global handlers (never blocked by scoped winners) ─────────────

    for (const def of globals) {
      if (def.preventDefault) event.preventDefault();
      if (def.stopPropagation) event.stopPropagation();
      def.handler(event);
      handled = true;
    }

    return handled;
  }

  // ─── Querying ──────────────────────────────────────────────────────────────

  getById(id: string): Readonly<ShortcutDefinition> | undefined {
    return this._shortcuts.get(id);
  }

  /**
   * Return all shortcuts optionally filtered by scope and/or scopeId.
   * Results are sorted by priority DESC then seq DESC.
   */
  getShortcuts(filter?: {
    scope?: ShortcutScope;
    scopeId?: string;
    enabled?: boolean;
  }): readonly ShortcutDefinition[] {
    const result: ShortcutDefinition[] = [];

    for (const def of this._shortcuts.values()) {
      if (filter?.scope !== undefined && def.scope !== filter.scope) continue;
      if (filter?.scopeId !== undefined && def.scopeId !== filter.scopeId) continue;
      if (filter?.enabled !== undefined && def.enabled !== filter.enabled) continue;
      result.push(def);
    }

    return result.sort((a, b) =>
      b.priority !== a.priority ? b.priority - a.priority : b.seq - a.seq,
    );
  }

  /**
   * Return shortcuts that have `meta` defined and `meta.visible !== false`.
   * The result is memoized and returns the SAME array reference until the
   * registry changes — required for useSyncExternalStore snapshot stability.
   */
  getVisibleShortcuts(): readonly ShortcutDefinition[] {
    if (this._visibleCache) return this._visibleCache;

    const result: ShortcutDefinition[] = [];

    for (const def of this._shortcuts.values()) {
      if (!def.enabled) continue;
      if (!def.meta) continue;
      if (def.meta.visible === false) continue;
      result.push(def);
    }

    result.sort((a, b) => (b.priority !== a.priority ? b.priority - a.priority : b.seq - a.seq));

    this._visibleCache = result;
    return this._visibleCache;
  }

  /**
   * Group visible shortcuts by `meta.group` for rendering in a help overlay.
   * Groups are ordered by the highest-priority shortcut they contain.
   */
  getVisibleShortcutGroups(): readonly ShortcutHelpGroup[] {
    const visible = this.getVisibleShortcuts();
    const groupMap = new Map<string, ShortcutDefinition[]>();

    for (const def of visible) {
      const group = def.meta?.group ?? 'General';
      const existing = groupMap.get(group);
      if (existing) {
        existing.push(def);
      } else {
        groupMap.set(group, [def]);
      }
    }

    return [...groupMap.entries()].map(([group, shortcuts]) => ({ group, shortcuts }));
  }

  // ─── Subscriptions ─────────────────────────────────────────────────────────

  /**
   * Subscribe to any registry change.
   * Returns an unsubscribe function — call it to clean up.
   *
   * Used by React's useSyncExternalStore in useShortcutHelp().
   */
  subscribe(listener: ChangeListener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  // ─── Debug ─────────────────────────────────────────────────────────────────

  /**
   * Print current registry state to the console.
   * Only call in development.
   */
  debug(): void {
    console.warn('[ShortcutRegistry] shortcuts:', [...this._shortcuts.values()]);
    console.warn('[ShortcutRegistry] activeScopes:', [...this._activeScopes]);
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _invalidateCache(): void {
    this._visibleCache = null;
  }

  private _emit(event: ShortcutChangeEvent): void {
    this._invalidateCache();
    for (const listener of this._listeners) {
      listener(event);
    }
  }
}
